import { Modal, ModalOverlay, ModalBody, Text, Center } from "@chakra-ui/react";
import { useState, useRef } from "react";
import {
  OtpInputBox,
  StyledInput,
  StyledModalContent,
  StyledVerifyButton,
  ResendText,
  DashSeparator,
  ResendClickableText,
} from "../styles";
import { useTranslation } from "react-i18next";
import { useGlobalContext } from "@root/context/global-context";

const EmailOtpModal = ({
  isOpen,
  onClose,
  onVerify,
  email,
}: EmailOtpModalProps) => {
  const [otp, setOtp] = useState<string[]>(["", "", "", "", "", ""]);
  const [hasError, setHasError] = useState(false);
  const { t } = useTranslation("common");
  const { verifyOtpLoading, verifyOtp, resendOtp } = useGlobalContext();

  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const handleChange = (value: string, index: number) => {
    if (!/^[0-9]?$/.test(value)) return;

    const updatedOtp = [...otp];
    updatedOtp[index] = value;
    setOtp(updatedOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleResendOtp = async () => {
    console.log("Resend clicked"); 
    await resendOtp({
      payload: {
        email,
      },
      onSuccess: () => {
        setOtp(["", "", "", "", "", ""]);
        setHasError(false);
      },
      onError: () => {
        setHasError(true);
      },
    });
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async () => {
    const fullOtp = otp.join("");
    const isValid = await verifyOtp({ otp: fullOtp.toString(), email });
    console.log(isValid);
    if (isValid) {
      setHasError(false);
      onVerify({ otp: Number(fullOtp), email });
    } else {
      setHasError(true);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      closeOnEsc={false}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <StyledModalContent>
        <ModalBody>
          <Center flexDirection="column" mt={6}>
            <Text fontSize="32px" fontWeight="600" color="#111827" mb={3}>
              {t("userProfile.verifyYourEmail")}
            </Text>

            <Text
              fontSize="16px"
              fontWeight="400px"
              color="#798692"
              mb={5}
              textAlign="center"
            >
              {t("userProfile.verificationCodeInstruction")}
            </Text>

            <OtpInputBox>
              {otp.map((digit, index) => (
                <Center key={index} gap="6px">
                  <StyledInput
                    ref={(el: HTMLInputElement | null) =>
                      (inputRefs.current[index] = el)
                    }
                    isInvalid={hasError}
                    value={digit}
                    maxLength={1}
                    onChange={(e) => handleChange(e.target.value, index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                  />
                  {index === 2 && <DashSeparator>-</DashSeparator>}
                </Center>
              ))}
            </OtpInputBox>
            {hasError && (
              <Text fontSize="14px" color="red" mb={2}>
                {t("userProfile.invalidCode")}
              </Text>
            )}

            <ResendText>
              {t("userProfile.didNotGetCode")}{" "}
              <ResendClickableText onClick={handleResendOtp}>
                {t("userProfile.resendCode")}
              </ResendClickableText>
            </ResendText>

            <StyledVerifyButton
              onClick={handleSubmit}
              mt={4}
              isDisabled={otp.some((digit) => digit === "")}
              isLoading={verifyOtpLoading}
            >
              {t("userProfile.verifyEmail")}
            </StyledVerifyButton>
          </Center>
        </ModalBody>
      </StyledModalContent>
    </Modal>
  );
};

export default EmailOtpModal;
